import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnSizingState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { ArrowDown, ArrowUp } from "lucide-react";
import type { TrackRow } from "#shared/schemas/trackQuery.ts";
import { STATUS_IDS } from "#shared/constants.ts";
import { cn } from "@web/lib/utils.ts";
import { useColumnStore } from "@web/stores/columns.ts";
import { useDetailStore } from "@web/stores/detail.ts";
import { useSelectionStore } from "@web/stores/selection.ts";
import { useUiStore } from "@web/stores/ui.ts";
import { useBrowseTrailStore } from "@web/stores/browseTrail.ts";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { useBulkStatus } from "@web/features/library/bulkApi.ts";
import { GridPlaybackProvider } from "@web/features/player/GridPlaybackContext.tsx";
import { usePlayerStore } from "@web/features/player/playerStore.ts";
import { queueTrackFromRow } from "@web/features/player/types.ts";
import {
  trackDragId,
  trackDropId,
  useLibraryDndOptional,
  type GridReorderEvent,
} from "@web/features/shell/libraryDnd.tsx";
import { type GridColumnMeta } from "./columns.tsx";
import { EditableCell } from "./EditableCell.tsx";
import "./grid.css";

const ROW_HEIGHT = { comfortable: 36, compact: 28 } as const;

/** Place `moving` as a block before/after `overId` in `ordered`. */
export function placeTrackIds(
  ordered: string[],
  moving: string[],
  overId: string,
  edge: "before" | "after",
): string[] {
  const moveSet = new Set(moving);
  if (moveSet.has(overId)) return ordered;
  const block = ordered.filter((id) => moveSet.has(id));
  if (block.length === 0) return ordered;
  const rest = ordered.filter((id) => !moveSet.has(id));
  const overIdx = rest.indexOf(overId);
  if (overIdx < 0) return ordered;
  const insertAt = edge === "before" ? overIdx : overIdx + 1;
  return [...rest.slice(0, insertAt), ...block, ...rest.slice(insertAt)];
}

function DraggableGridRow({
  trackId,
  focused,
  selected,
  rowIndex,
  height,
  transformY,
  dropEdge,
  onRowClick,
  onRowOpen,
  children,
}: {
  trackId: string;
  focused: boolean;
  selected: boolean;
  rowIndex: number;
  height: number;
  transformY: number;
  dropEdge: "before" | "after" | null;
  onRowClick: (e: React.MouseEvent, id: string) => void;
  onRowOpen: (id: string) => void;
  children: React.ReactNode;
}) {
  const dnd = useLibraryDndOptional();

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: trackDragId(trackId),
    data: dnd?.buildTracksDrag(trackId) ?? {
      kind: "tracks" as const,
      trackIds: [trackId],
      label: "1 track",
      count: 1,
    },
    disabled: !dnd,
  });

  const dropId = trackDropId(trackId);
  const { setNodeRef: setDropRef } = useDroppable({
    id: dropId,
    data: { kind: "track-row" as const, trackId },
    // Keep measured even before activeDrag flips — otherwise mid-drag
    // collision misses rows until a second render.
    disabled: !dnd,
  });

  function setRefs(node: HTMLDivElement | null) {
    setDragRef(node);
    setDropRef(node);
  }

  return (
    <div
      ref={setRefs}
      className={cn("coykoi-grid-row", isDragging && "opacity-40")}
      data-focused={focused ? "true" : "false"}
      data-track-drop={dropId}
      data-drop-edge={dropEdge ?? undefined}
      aria-selected={selected}
      aria-rowindex={rowIndex}
      style={{
        height,
        transform: `translateY(${transformY}px)`,
      }}
      onClick={(ev) => onRowClick(ev, trackId)}
      onDoubleClick={(ev) => {
        // Play / status editors own their own double-click; don't steal it.
        if ((ev.target as HTMLElement).closest("button, select, input, a")) return;
        onRowOpen(trackId);
      }}
      {...listeners}
      {...attributes}
    >
      {children}
    </div>
  );
}

export function DataGrid({
  rows: serverRows,
  columns,
  scopeKey,
  scope,
  q,
  total,
  sorting,
  onSortingChange,
  onReorder,
  onReachEnd,
  hasMore,
  loadingMore,
}: {
  rows: TrackRow[];
  columns: ColumnDef<TrackRow, unknown>[];
  scopeKey: string;
  scope: string;
  q: string;
  total: number;
  sorting: SortingState;
  onSortingChange: (next: SortingState) => void;
  /** Called after an in-grid drop. Persist for local playlists; UI order already applied. */
  onReorder?: (event: GridReorderEvent) => void;
  onReachEnd?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
}) {
  const density = useUiStore((s) => s.density);
  const rowHeight = ROW_HEIGHT[density];
  const prefs = useColumnStore((s) => s.byScope[scopeKey]);
  const patchPrefs = useColumnStore((s) => s.patchPrefs);

  /** User-mode defaults: Year + Date added optional (column picker persists). */
  const defaultVisibility: VisibilityState = {
    "album.release_year": false,
    "track.added_at": false,
  };

  const mode = useSelectionStore((s) => s.mode);
  const selectOnly = useSelectionStore((s) => s.selectOnly);
  const toggle = useSelectionStore((s) => s.toggle);
  const selectRange = useSelectionStore((s) => s.selectRange);
  const moveFocusSel = useSelectionStore((s) => s.moveFocus);
  const extendTo = useSelectionStore((s) => s.extendTo);
  const selectAllFiltered = useSelectionStore((s) => s.selectAllFiltered);
  const clear = useSelectionStore((s) => s.clear);
  const isSelected = useSelectionStore((s) => s.isSelected);
  const bulkTarget = useSelectionStore((s) => s.bulkTarget);
  const setStatus = useBulkStatus();

  const focusedId = mode?.focusId ?? null;

  /** Client order override (temporary except when `onReorder` persists it). */
  const [orderOverride, setOrderOverride] = useState<string[] | null>(null);

  useEffect(() => {
    setOrderOverride(null);
  }, [scopeKey, q]);

  useEffect(() => {
    if (sorting.length > 0) setOrderOverride(null);
  }, [sorting]);

  const rows = useMemo(() => {
    if (!orderOverride) return serverRows;
    const byId = new Map(serverRows.map((r) => [r.id, r]));
    const ordered: TrackRow[] = [];
    const seen = new Set<string>();
    for (const id of orderOverride) {
      const row = byId.get(id);
      if (row) {
        ordered.push(row);
        seen.add(id);
      }
    }
    for (const row of serverRows) {
      if (!seen.has(row.id)) ordered.push(row);
    }
    return ordered;
  }, [serverRows, orderOverride]);

  const orderedIds = useMemo(() => rows.map((r) => r.id), [rows]);

  const dnd = useLibraryDndOptional();
  const dropHint = dnd?.dropHint ?? null;
  const overId = dnd?.overId ?? null;
  const onReorderRef = useRef(onReorder);
  onReorderRef.current = onReorder;
  const onSortingChangeRef = useRef(onSortingChange);
  onSortingChangeRef.current = onSortingChange;
  const serverRowsRef = useRef(serverRows);
  serverRowsRef.current = serverRows;
  const sortingRef = useRef(sorting);
  sortingRef.current = sorting;

  useEffect(() => {
    if (!dnd) return;
    return dnd.registerGridReorderHandler((event) => {
      setOrderOverride((prev) => {
        const base = prev ?? serverRowsRef.current.map((r) => r.id);
        return placeTrackIds(base, event.trackIds, event.overTrackId, event.edge);
      });
      if (sortingRef.current.length > 0) onSortingChangeRef.current([]);
      onReorderRef.current?.(event);
    });
  }, [dnd]);

  const playbackApi = useMemo(
    () => ({
      rowsAsQueue: () =>
        rows
          .map(queueTrackFromRow)
          .filter((t): t is NonNullable<typeof t> => t !== null),
      playFromTrack: (startTrackId: string) => {
        const queue = rows
          .map(queueTrackFromRow)
          .filter((t): t is NonNullable<typeof t> => t !== null);
        void usePlayerStore.getState().playTracks(queue, startTrackId);
      },
    }),
    [rows],
  );

  // Pending target follows grid focus. Trail resets only when focus moves to a
  // different row while the sheet is open — not on every `rows` identity change
  // (that was wiping the forward stack after Back).
  const setDetailTarget = useDetailStore((s) => s.setTarget);
  const openDetail = useOpenDetail();
  const sheetOpen = useUiStore((s) => s.sheetOpen);
  const prevFocusForTrail = useRef<string | null>(null);

  useEffect(() => {
    if (!focusedId) return;
    setDetailTarget({ type: "track", id: focusedId });
  }, [focusedId, setDetailTarget]);

  useEffect(() => {
    if (!sheetOpen || !focusedId) {
      prevFocusForTrail.current = focusedId;
      return;
    }
    if (prevFocusForTrail.current === focusedId) return;
    prevFocusForTrail.current = focusedId;
    const row = rows.find((r) => r.id === focusedId);
    useBrowseTrailStore.getState().resetTo({
      type: "track",
      id: focusedId,
      label: row?.name ?? focusedId,
    });
  }, [focusedId, sheetOpen, rows]);

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(prefs?.widths ?? {});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    ...defaultVisibility,
    ...(prefs?.visibility ?? {}),
  });

  useEffect(() => {
    setColumnSizing(prefs?.widths ?? {});
    setColumnVisibility({
      ...defaultVisibility,
      ...(prefs?.visibility ?? {}),
    });
  }, [scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handle = window.setTimeout(() => {
      patchPrefs(scopeKey, { widths: columnSizing, visibility: columnVisibility });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [columnSizing, columnVisibility, patchPrefs, scopeKey]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnSizing, columnVisibility },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      onSortingChange(next);
    },
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    columnResizeMode: "onChange",
    getRowId: (row) => row.id,
  });

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  useEffect(() => {
    const last = virtualRows.at(-1);
    if (!last || !onReachEnd || !hasMore || loadingMore) return;
    if (last.index >= rows.length - 40) onReachEnd();
  }, [virtualRows, onReachEnd, hasMore, loadingMore, rows.length]);

  const headerGroups = table.getHeaderGroups();
  const leafColumns = table.getVisibleLeafColumns();
  const totalWidth = Math.max(
    leafColumns.reduce((sum, col) => sum + col.getSize(), 0),
    1,
  );

  function columnStyle(columnMeta: GridColumnMeta, size: number): React.CSSProperties {
    if (columnMeta.flex) {
      return { flex: `${columnMeta.flex} 1 0%`, minWidth: size };
    }
    return { flex: `0 0 ${size}px`, width: size };
  }

  const focusIndex = useMemo(
    () => (focusedId ? rows.findIndex((r) => r.id === focusedId) : -1),
    [focusedId, rows],
  );

  const moveFocus = useCallback(
    (delta: number, extend: boolean) => {
      if (rows.length === 0) return;
      const current = focusIndex < 0 ? 0 : focusIndex;
      const next = Math.max(0, Math.min(rows.length - 1, current + delta));
      const row = rows[next];
      if (!row) return;
      if (extend) extendTo(row.id, orderedIds);
      else moveFocusSel(row.id);
      virtualizer.scrollToIndex(next, { align: "auto" });
    },
    [extendTo, focusIndex, moveFocusSel, orderedIds, rows, virtualizer],
  );

  function onRowClick(e: React.MouseEvent, id: string) {
    if (e.shiftKey) selectRange(id, orderedIds);
    else if (e.metaKey || e.ctrlKey) toggle(id);
    else selectOnly(id);
  }

  function onRowOpen(id: string) {
    selectOnly(id);
    const row = rows.find((r) => r.id === id);
    openDetail(
      { type: "track", id, label: row?.name ?? id },
      { mode: "reset" },
    );
  }

  return (
    <GridPlaybackProvider value={playbackApi}>
      <div
        className="coykoi-grid"
        data-density={density}
        tabIndex={0}
        role="grid"
        aria-rowcount={rows.length}
        onKeyDown={(e) => {
          const tag = (e.target as HTMLElement).tagName;
          if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

          if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a") {
            e.preventDefault();
            selectAllFiltered(scope, q, total);
            return;
          }

          if (e.key === "Escape") {
            e.preventDefault();
            clear();
            return;
          }

          if (e.key === "ArrowDown" || e.key === "j") {
            e.preventDefault();
            moveFocus(1, e.shiftKey);
          } else if (e.key === "ArrowUp" || e.key === "k") {
            e.preventDefault();
            moveFocus(-1, e.shiftKey);
          } else if (e.key === "Home") {
            e.preventDefault();
            moveFocus(-rows.length, e.shiftKey);
          } else if (e.key === "End") {
            e.preventDefault();
            moveFocus(rows.length, e.shiftKey);
          } else if (e.key === "Enter" && focusedId) {
            e.preventDefault();
            onRowOpen(focusedId);
          } else if (e.key.toLowerCase() === "x" && focusedId) {
            e.preventDefault();
            toggle(focusedId);
          } else if (/^[1-7]$/.test(e.key)) {
            const status = STATUS_IDS[Number(e.key) - 1];
            const target = bulkTarget();
            if (status && target) {
              e.preventDefault();
              setStatus.mutate({ status, target });
            }
          }
        }}
      >
        <div ref={parentRef} className="coykoi-grid-scroll">
          <div className="coykoi-grid-table" style={{ minWidth: `max(100%, ${totalWidth}px)` }}>
            {headerGroups.map((headerGroup) => (
              <div key={headerGroup.id} className="coykoi-grid-header">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const columnMeta = (header.column.columnDef.meta ?? {}) as GridColumnMeta;
                  return (
                    <div
                      key={header.id}
                      className="coykoi-grid-header-cell"
                      style={columnStyle(columnMeta, header.getSize())}
                      data-sortable={canSort ? "true" : "false"}
                      data-sorted={sorted ? "true" : "false"}
                      onClick={header.column.getToggleSortingHandler()}
                      role="columnheader"
                      aria-sort={
                        sorted === "asc"
                          ? "ascending"
                          : sorted === "desc"
                            ? "descending"
                            : "none"
                      }
                    >
                      <span className="truncate">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {sorted === "asc" && <ArrowUp className="size-3 shrink-0" />}
                      {sorted === "desc" && <ArrowDown className="size-3 shrink-0" />}
                      {header.column.getCanResize() && (
                        <div
                          className="coykoi-grid-resize"
                          data-resizing={header.column.getIsResizing() ? "true" : "false"}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onClick={(ev) => ev.stopPropagation()}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            <div className="coykoi-grid-body" style={{ height: totalSize }}>
              {virtualRows.map((virtualRow) => {
                const row = table.getRowModel().rows[virtualRow.index];
                if (!row) return null;
                const focused = row.id === focusedId;
                const selected = isSelected(row.id);
                const rowDropId = trackDropId(row.id);
                const dropEdge =
                  overId === rowDropId && (dropHint === "before" || dropHint === "after")
                    ? dropHint
                    : null;
                return (
                  <DraggableGridRow
                    key={row.id}
                    trackId={row.id}
                    focused={focused}
                    selected={selected}
                    rowIndex={virtualRow.index + 1}
                    height={virtualRow.size}
                    transformY={virtualRow.start}
                    dropEdge={dropEdge}
                    onRowClick={onRowClick}
                    onRowOpen={onRowOpen}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const cellMeta = (cell.column.columnDef.meta ?? {}) as GridColumnMeta;
                      const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());
                      return (
                        <div
                          key={cell.id}
                          className={cn("coykoi-grid-cell")}
                          style={columnStyle(cellMeta, cell.column.getSize())}
                          data-align={cellMeta.align ?? "left"}
                          role="gridcell"
                          onClick={(ev) => {
                            if ((ev.target as HTMLElement).closest("button, select, input")) {
                              ev.stopPropagation();
                            }
                          }}
                        >
                          <EditableCell track={row.original} meta={cellMeta} display={rendered} />
                        </div>
                      );
                    })}
                  </DraggableGridRow>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </GridPlaybackProvider>
  );
}
